import { Fragment, type CSSProperties, type ImgHTMLAttributes } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import ImageLightboxPreviewTrigger, {
  createLightboxPreviewItem,
} from '@/components/media/ImageLightboxPreviewTrigger'
import { ChevronDown } from 'lucide-react'
import ImageLightboxGallery, { type LightboxItem } from '@/components/media/ImageLightboxGallery'
import { Checkbox, type CheckboxProps } from '../ui/checkbox'
import { Label } from '../ui/label'
import { Separator } from '../ui/separator'
import CollectionBadges from './CollectionBadges'
import ProductBadges from './ProductBadges'
import type { ImportCollection } from './collection-import.types'

export interface CollectionCardProps {
  collection: ImportCollection
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  setIsCollectionSelected: NonNullable<CheckboxProps['onCheckedChange']>
  isProductSelected: readonly NonNullable<CheckboxProps['checked']>[]
  setIsProductSelected: (index: number) => NonNullable<CheckboxProps['onCheckedChange']>
  collectionImageLoading?: ImgHTMLAttributes<HTMLImageElement>['loading']
}

const MAX_COLLECTION_PREVIEW_WIDTH = 96
const MAX_COLLECTION_PREVIEW_HEIGHT = 64

function getCollectionPreviewStyle(
  media: ImportCollection['media'][number] | null | undefined
): CSSProperties {
  const width = Number(media?.width)
  const height = Number(media?.height)

  if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
    return {
      width: `${MAX_COLLECTION_PREVIEW_WIDTH}px`,
      height: `${MAX_COLLECTION_PREVIEW_HEIGHT}px`,
    }
  }

  const scale = Math.min(
    MAX_COLLECTION_PREVIEW_WIDTH / width,
    MAX_COLLECTION_PREVIEW_HEIGHT / height
  )

  return {
    width: `${Math.round(width * scale)}px`,
    height: `${Math.round(height * scale)}px`,
  }
}

const CollectionCard = ({
  collection,
  isOpen,
  setIsOpen,
  setIsCollectionSelected,
  isProductSelected,
  setIsProductSelected,
  collectionImageLoading = 'lazy',
}: CollectionCardProps) => {
  const collectionMedia = collection?.media?.[0]
  const previewItems: (LightboxItem | null)[] = []

  let collectionPreviewIndex: number | null = null

  if (collectionMedia?.url) {
    collectionPreviewIndex = previewItems.length
    previewItems.push(
      createLightboxPreviewItem({
        id: `collection-${collection.id}`,
        media: collectionMedia,
        alt: collection.name,
        caption: collection.name,
      })
    )
  }

  const productsWithPreviews = collection.products.map((product) => {
    const previewItem = createLightboxPreviewItem({
      id: `product-${product.id}`,
      media: product?.media?.[0],
      alt: product.name,
      caption: product.name,
    })

    const previewIndex = previewItem ? previewItems.length : null

    if (previewItem) previewItems.push(previewItem)

    return { product, previewIndex }
  })

  // Determine the checked state of the "collection" checkbox based on the checked state of the "product" checkboxes
  const getCheckedState = () => {
    if (collection.hasNothingToImport) {
      return false
    }
    if (
      // If every non-disabled product is selected, then the collection is "completely" selected
      isProductSelected.every(
        (isSelected, productIdx) => collection.products[productIdx]?.disabled || isSelected
      )
    ) {
      return true
    }
    if (isProductSelected.some((isSelected) => isSelected)) {
      return 'indeterminate'
    }

    return false
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <ImageLightboxGallery items={previewItems}>
        {({ getTriggerProps }) => (
          <Card>
            <CollapsibleTrigger asChild className="group">
              <CardHeader className="cursor-pointer">
                <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 @sm/card-header:grid-cols-[auto_auto_minmax(0,1fr)_auto] @3xl/card-header:grid-cols-[auto_auto_minmax(0,1fr)_auto_auto]">
                  <Checkbox
                    onClick={(e) => e.stopPropagation()}
                    checked={getCheckedState()}
                    disabled={collection.hasNothingToImport}
                    onCheckedChange={setIsCollectionSelected}
                  />
                  <ImageLightboxPreviewTrigger
                    media={collectionMedia}
                    alt={collection.name}
                    caption={collection.name}
                    triggerProps={
                      collectionPreviewIndex === null
                        ? null
                        : getTriggerProps(collectionPreviewIndex)
                    }
                    frameClassName="bg-muted/20"
                    frameStyle={getCollectionPreviewStyle(collectionMedia)}
                    imageLoading={collectionImageLoading}
                    imageClassName="object-contain"
                  />
                  <CardTitle className="col-span-3 min-w-0 break-words @sm/card-header:col-span-1 @sm/card-header:col-start-3 @sm/card-header:row-start-1 @3xl/card-header:truncate">
                    {collection.name}
                  </CardTitle>
                  <div className="col-span-3 min-w-0 @sm/card-header:col-span-4 @3xl/card-header:col-span-1 @3xl/card-header:col-start-4 @3xl/card-header:row-start-1">
                    <CollectionBadges collection={collection} />
                  </div>
                  <ChevronDown className="col-start-3 row-start-1 shrink-0 transition-transform group-data-[state=open]:rotate-180 @sm/card-header:col-start-4 @3xl/card-header:col-start-5" />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="@container/collection-products">
                <ul className="my-2 space-y-2">
                  {productsWithPreviews.map(({ product, previewIndex }, productIdx) => (
                    <Fragment key={product.id}>
                      <li className="flex flex-col gap-3 py-1 @sm/collection-products:flex-row @sm/collection-products:items-center @sm/collection-products:justify-between">
                        <div className="flex min-w-0 items-center gap-3">
                          <Checkbox
                            id={`${collection.id}-${product.id}`}
                            disabled={product.disabled}
                            checked={isProductSelected[productIdx]}
                            onCheckedChange={setIsProductSelected(productIdx)}
                          />
                          <ImageLightboxPreviewTrigger
                            media={product?.media?.[0]}
                            alt={product?.name}
                            caption={product?.name}
                            triggerProps={
                              previewIndex === null ? null : getTriggerProps(previewIndex)
                            }
                            frameClassName="h-16 w-16 border bg-muted/50 shadow-sm"
                            imageClassName="object-cover"
                          />
                          <Label
                            htmlFor={`${collection.id}-${product.id}`}
                            appearance="choice"
                            weight="normal"
                            className="min-w-0 grow"
                          >
                            <span className="block break-words @sm/collection-products:truncate">
                              {product?.name}
                            </span>
                          </Label>
                        </div>
                        <ProductBadges product={product} />
                      </li>
                      {productIdx !== collection?.products?.length - 1 && <Separator />}
                    </Fragment>
                  ))}
                </ul>
              </CardContent>
            </CollapsibleContent>
          </Card>
        )}
      </ImageLightboxGallery>
    </Collapsible>
  )
}

export default CollectionCard
